export const PRICE_MODES = {
  INCLUSIVE: 'INCLUSIVE',
  EXCLUSIVE: 'EXCLUSIVE',
};

export const DEFAULT_GST_RATE = 0;

export const GST_RATE_OPTIONS = [0, 5, 12, 18, 28];

export const toAmount = (value, fallback = 0) => {
  const cleaned = String(value ?? '')
    .replace(/[₹,%]/g, '')
    .replace(/,/g, '')
    .trim();

  const number = Number(cleaned);
  return Number.isFinite(number) ? number : fallback;
};

export const roundMoney = (value) => (
  Math.round((toAmount(value) + Number.EPSILON) * 100) / 100
);

export const clampRate = (value, fallback = DEFAULT_GST_RATE) => {
  const number = toAmount(value, fallback);
  return Math.min(100, Math.max(0, number));
};

export const hasValidGstin = (value) => (
  /^[0-9A-Z]{15}$/.test(
    String(value ?? '').trim().toUpperCase()
  )
);

export const isGstApplicable = ({
  gstEnabled = true,
  gstin = '',
  registrationType = 'REGISTERED',
  gstRate = DEFAULT_GST_RATE,
} = {}) => {
  return Boolean(
    gstEnabled &&
    String(registrationType).toUpperCase() !== 'UNREGISTERED' &&
    hasValidGstin(gstin) &&
    clampRate(gstRate) > 0
  );
};

export const normalisePriceMode = (
  value,
  fallback = PRICE_MODES.EXCLUSIVE
) => {
  if (value === true) return PRICE_MODES.INCLUSIVE;
  if (value === false) return PRICE_MODES.EXCLUSIVE;

  const mode = String(value ?? '').trim().toLowerCase();

  if (
    mode.includes('inclusive') ||
    mode.includes('include') ||
    mode.includes('with gst') ||
    mode.includes('including gst') ||
    mode === 'inc' ||
    mode === 'yes' ||
    mode === 'true'
  ) {
    return PRICE_MODES.INCLUSIVE;
  }

  if (
    mode.includes('exclusive') ||
    mode.includes('exclude') ||
    mode.includes('without gst') ||
    mode.includes('excluding gst') ||
    mode === 'exc' ||
    mode === 'no' ||
    mode === 'false'
  ) {
    return PRICE_MODES.EXCLUSIVE;
  }

  return fallback;
};

export const getDiscountAmount = ({
  price = 0,
  discountActive = false,
  discountType = 'Percentage',
  discountValue = 0,
}) => {
  if (!discountActive) return 0;

  const amount = Math.max(0, toAmount(price));
  const value = Math.max(0, toAmount(discountValue));

  const discount = discountType === 'Flat'
    ? value
    : (amount * Math.min(value, 100)) / 100;

  return Math.min(amount, discount);
};

/**
 * GST pricing rules:
 *
 * EXCLUSIVE:
 * entered price = taxable price
 * GST is added on top
 *
 * INCLUSIVE:
 * entered price = customer-facing price
 * taxable price = entered price / (1 + GST rate)
 * GST is extracted from entered price
 *
 * Example:
 * Inclusive ₹1180 @ 18%
 * Taxable ₹1000 + GST ₹180 = Final ₹1180
 */
export const calculatePricing = ({
  price = 0,
  priceMode = PRICE_MODES.EXCLUSIVE,
  gstRate = DEFAULT_GST_RATE,
  gstApplicable = true,
  qty = 1,
  discountActive = false,
  discountType = 'Percentage',
  discountValue = 0,
}) => {
  const quantity = Math.max(0, toAmount(qty, 1));
  const enteredUnitPrice = Math.max(0, toAmount(price));
  const mode = normalisePriceMode(priceMode);

  // GST will become zero when GSTIN is missing or salon is unregistered.
  const rate = gstApplicable ? clampRate(gstRate) : 0;

  const discountPerUnit = getDiscountAmount({
    price: enteredUnitPrice,
    discountActive,
    discountType,
    discountValue,
  });

  const adjustedUnitPrice = Math.max(
    0,
    enteredUnitPrice - discountPerUnit
  );

  const enteredAmount = adjustedUnitPrice * quantity;

  // This is the rate that should be shown in invoice Rate column.
  const taxableUnitPrice = (
    mode === PRICE_MODES.INCLUSIVE && rate > 0
  )
    ? enteredUnitPrice / (1 + rate / 100)
    : enteredUnitPrice;

  let taxableAmount = enteredAmount;
  let gstAmount = 0;
  let finalAmount = enteredAmount;

  if (mode === PRICE_MODES.INCLUSIVE && rate > 0) {
    // GST is already included in customer price.
    taxableAmount = enteredAmount / (1 + rate / 100);
    gstAmount = enteredAmount - taxableAmount;
    finalAmount = enteredAmount;
  }

  if (mode === PRICE_MODES.EXCLUSIVE && rate > 0) {
    // GST is added to taxable amount.
    taxableAmount = enteredAmount;
    gstAmount = taxableAmount * (rate / 100);
    finalAmount = taxableAmount + gstAmount;
  }

  return {
    quantity,
    priceMode: mode,
    gstRate: rate,

    enteredUnitPrice: roundMoney(enteredUnitPrice),
    enteredAmount: roundMoney(enteredAmount),

    discountPerUnit: roundMoney(discountPerUnit),
    discountAmount: roundMoney(discountPerUnit * quantity),

    adjustedUnitPrice: roundMoney(adjustedUnitPrice),

    // Use this value in Invoice Rate column.
    taxableUnitPrice: roundMoney(taxableUnitPrice),

    taxableUnitPriceAfterDiscount: roundMoney(
      quantity > 0 ? taxableAmount / quantity : 0
    ),

    taxableAmount: roundMoney(taxableAmount),
    gstAmount: roundMoney(gstAmount),
    finalAmount: roundMoney(finalAmount),
  };
};

export const formatCurrency = (
  value,
  minimumFractionDigits = 2
) => {
  return `₹${toAmount(value).toLocaleString('en-IN', {
    minimumFractionDigits,
    maximumFractionDigits: 2,
  })}`;
};